/**
 * @fileoverview Mock-only in-memory unified sales-user roster.
 * @description Seeds from sales-users-seed.json and holds it in a module-level array, same
 * in-memory-only pattern as tenants-mock-store.ts. This is the single roster both the
 * tenants/targets mock stores (ownership, hierarchy scoping) and sales-roster-mock.service.ts read
 * from — see
 * SalesUserRecord's own file comment for why it replaces those two drifting fixtures. Never
 * imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  canManageSalesUser,
  creatableRolesFor,
  eligibleReportingManagersFor,
  wouldCreateReportingCycle,
} from '@/features/sales/lib/sales-hierarchy.utils'

import seedData from './sales-users-seed.json'

import type { UpdateSalesUserInput } from '@/features/sales/types/sales-roster.types'
import type { TeamRole } from '@/features/sales/types/sales.types'
import type { SalesUserRecord, SalesUserStatus } from '@/features/sales/types/tenant.types'

const roster: SalesUserRecord[] = seedData.map((user) => ({
  ...user,
  role: user.role as SalesUserRecord['role'],
  status: user.status as SalesUserRecord['status'],
}))

/**
 * @description Returns the full sales-user roster.
 * @returns {SalesUserRecord[]} Every provisioned sales user.
 */
export function listSalesRoster(): SalesUserRecord[] {
  return roster
}

/**
 * @description Finds one sales user by id.
 * @param {string} id - The sales user id to look up.
 * @returns {SalesUserRecord | undefined} The matching user, or undefined.
 */
export function findSalesUserById(id: string): SalesUserRecord | undefined {
  return roster.find((user) => user.id === id)
}

interface ActingUser {
  id: string
  role: string
}

/**
 * @description Validates a proposed reportsTo change for updateSalesUser, isolated purely to keep
 * that function's branching within this codebase's complexity limit.
 * @param {string} id - The user whose reportsTo is changing.
 * @param {TeamRole} nextRole - The user's role after this update is applied.
 * @param {string | null} nextReportsTo - The proposed new reporting-manager id.
 * @param {ActingUser} actingUser - The administrator making the change.
 * @returns {string | null} An error message, or null if the change is valid.
 */
function validateReportsToChange(
  id: string,
  nextRole: TeamRole,
  nextReportsTo: string | null,
  actingUser: ActingUser,
): string | null {
  if (nextRole === 'Sales Head') {
    return nextReportsTo === null ? null : 'A Sales Head has no reporting manager.'
  }
  if (!nextReportsTo) {
    return 'Select a reporting manager within your authorised scope.'
  }
  if (wouldCreateReportingCycle(id, nextReportsTo, roster)) {
    return 'This reporting change would create a circular hierarchy.'
  }
  const eligibleIds = eligibleReportingManagersFor(
    nextRole,
    actingUser.id,
    actingUser.role,
    roster,
  ).map((candidate) => candidate.id)
  return eligibleIds.includes(nextReportsTo)
    ? null
    : 'Select a reporting manager within your authorised scope.'
}

/**
 * @description Updates a sales user's editable profile and reporting details. Enforces "cannot
 * manage accounts outside their authorised hierarchy", that a changed role stays one the acting
 * administrator is authorised to assign, and "Reporting relationships must not create circular
 * hierarchies". Status is deliberately not settable here — see setSalesUserStatus and
 * sales-roster-mock.service.ts's deactivate/reactivate orchestration, which carry side effects this
 * plain field update must not trigger.
 * @param {string} id - The sales user id to update.
 * @param {UpdateSalesUserInput} input - The fields to change (only supplied fields are touched).
 * @param {string} actingUserId - The administrator making the change.
 * @param {string} actingUserRole - The administrator's active role name.
 * @returns {SalesUserRecord | { error: string } | null} The updated user; an error object for a
 * permission or hierarchy violation; or null if no user matched the id.
 */
export function updateSalesUser(
  id: string,
  input: UpdateSalesUserInput,
  actingUserId: string,
  actingUserRole: string,
): SalesUserRecord | { error: string } | null {
  const user = findSalesUserById(id)
  if (!user) return null

  if (!canManageSalesUser(actingUserId, actingUserRole, id, roster)) {
    return { error: 'This user falls outside your authorised hierarchy.' }
  }

  if (input.role && !creatableRolesFor(actingUserRole).includes(input.role)) {
    return { error: 'You are not authorised to assign this role.' }
  }

  const emailTaken = roster.some(
    (candidate) =>
      candidate.id !== id && candidate.email.toLowerCase() === input.email?.toLowerCase(),
  )
  if (input.email && emailTaken) {
    return { error: 'A user with this email already exists.' }
  }

  if (input.reportsTo !== undefined) {
    // `user.role` can only still be 'Super Admin' here if `canManageSalesUser` above had wrongly
    // allowed editing that record — it never does (no one's downline includes Super Admin, and they
    // can't manage themselves either), so this cast only ever narrows a value that's already a
    // `TeamRole` at runtime.
    const nextRole = (input.role ?? user.role) as TeamRole
    const reportsToError = validateReportsToChange(id, nextRole, input.reportsTo, {
      id: actingUserId,
      role: actingUserRole,
    })
    if (reportsToError) {
      return { error: reportsToError }
    }
  }

  Object.assign(user, input)
  return user
}

/**
 * @description Sets a sales user's status directly, with no side effects of its own — a plain
 * primitive reused by both the deactivate/reactivate orchestration in
 * sales-roster-mock.service.ts (which performs the target-transfer and reparenting side effects
 * first) and, for Pending Invitation users, by an eventual "accept invitation" step. Deliberately
 * does not live in that orchestration layer itself so a reactivation really can be "a pure status
 * flip... must not reverse transfers or alter completion credit" without this function being tempted
 * to do more.
 * @param {string} id - The sales user id to update.
 * @param {SalesUserStatus} status - The new status.
 * @returns {SalesUserRecord | null} The updated user, or null if no user matched the id.
 */
export function setSalesUserStatus(id: string, status: SalesUserStatus): SalesUserRecord | null {
  const user = findSalesUserById(id)
  if (!user) return null
  user.status = status
  return user
}

/**
 * @description Reassigns every direct report of `fromUserId` to report to `toUserId` instead —
 * Deactivate's "Executives reporting to a deactivated Sales Manager are reassigned to the Manager's
 * own reporting Sales Head" (and, symmetrically, usable for a Sales Head's Managers once a
 * successor Head is chosen). Pure roster mutation only; never called directly by a page or hook —
 * see sales-roster-mock.service.ts's deactivation orchestration, which decides `toUserId` and pairs
 * this with transferOutstandingTargets.
 * @param {string} fromUserId - The user whose direct reports are being reparented.
 * @param {string} toUserId - The new reporting manager for those reports.
 * @returns {SalesUserRecord[]} Every report that was reparented.
 */
export function reparentDirectReports(fromUserId: string, toUserId: string): SalesUserRecord[] {
  const reports = roster.filter((user) => user.reportsTo === fromUserId)
  for (const user of reports) {
    user.reportsTo = toUserId
  }
  return reports
}
